/**
 * @file PrimaryButton.tsx
 * @module Components/Button
 * @description Premium, kiosk-optimized button with depth.
 * Update: Enhanced disabled state for institutional form validation.
 */

import React, { useState } from "react";
import {
  TouchableOpacity,
  Text,
  ActivityIndicator,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
  View,
  Platform,
} from "react-native";
import { FontFamily } from "../theme/typography";
import { useTheme } from "../context/themeContext";

/* ================= TYPES ================= */

export type ButtonVariant = 'primary' | 'outline' | 'danger' | 'dangerSoft' | 'cancel' | 'ghost' | 'google';
export type ButtonSize = 'large' | 'small';

type PrimaryButtonProps = Readonly<{
  title: string;
  loading?: boolean;
  disabled?: boolean;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  testID?: string;
  // Premium hover lift on web for any variant (subtle translate + shadow).
  hoverLift?: boolean;
}>;

/* ================= COMPONENT ================= */

export default function PrimaryButton({
  title,
  loading = false,
  disabled = false,
  onPress,
  variant = 'primary',
  size = 'large',
  style,
  textStyle,
  leftIcon,
  rightIcon,
  textTransform,
  testID,
  hoverLift = false,
}: PrimaryButtonProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const isInactive = disabled || loading;
  const [hovered, setHovered] = useState(false);

  const wantsHover = variant === 'google' || variant === 'dangerSoft' || hoverLift;

  // Theme-adaptive color layer per variant. Non-color style props (size,
  // padding, shadow geometry, web transitions) stay in the static StyleSheet.
  const variantColor: Record<ButtonVariant, ViewStyle> = {
    primary: { backgroundColor: '#0B0B0D', borderColor: '#0B0B0D', shadowColor: '#000000' },
    outline: { backgroundColor: colors.transparent, borderColor: colors.line },
    danger: { backgroundColor: colors.danger, borderColor: colors.danger, shadowColor: colors.danger },
    cancel: { backgroundColor: colors.transparent, borderColor: colors.dangerLine },
    dangerSoft: { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine },
    ghost: { backgroundColor: colors.transparent },
    google: { backgroundColor: '#4285F4', borderColor: colors.transparent, shadowColor: '#4285F4' },
  };
  const variantTextColor: Record<ButtonVariant, string> = {
    // Fixed near-black button with white text in both themes.
    primary: colors.white,
    outline: colors.ink2,
    danger: colors.white,
    cancel: colors.danger,
    dangerSoft: colors.danger,
    ghost: colors.ink2,
    google: colors.white,
  };
  const dangerSoftHoverColor: ViewStyle = { backgroundColor: colors.dangerSoft, borderColor: colors.danger };

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isInactive}
      activeOpacity={0.82}
      testID={testID}
      style={[
        styles.buttonBase,
        { borderColor: colors.line },
        styles[variant as keyof typeof styles] as ViewStyle,
        variantColor[variant],
        size === 'small' && styles.small,
        (variant === 'google' || variant === 'dangerSoft') && hovered &&
          (styles[`${variant}Hovered` as keyof typeof styles] as ViewStyle),
        variant === 'dangerSoft' && hovered && dangerSoftHoverColor,
        style,
        // Generic premium lift (applied AFTER `style` so it layers on any colour).
        hoverLift && hovered && !isInactive && styles.genericHover,
        // Disabled uses muted theme tokens; layered last so it always wins.
        isInactive && { backgroundColor: colors.line, borderColor: colors.line, elevation: 0, shadowOpacity: 0 },
      ]}
      {...(Platform.OS === 'web' && wantsHover && !isInactive ? {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      } : {})}
    >
      <View style={styles.contentWrapper}>
        {loading ? (
          <ActivityIndicator color={variantTextColor[variant]} size="small" />
        ) : (
          <View style={styles.innerContent}>
            {/* Drop the icon's gap when there's no label so an icon-only
                button stays perfectly centered. */}
            {leftIcon && <View style={[styles.iconLeft, !title && { marginRight: 0 }]}>{leftIcon}</View>}
            {title ? (
              <Text style={[
                styles.text,
                styles[`${variant}Text` as keyof typeof styles],
                { color: variantTextColor[variant] },
                size === 'small' && styles.smallText,
                textTransform && { textTransform },
                textStyle,
                isInactive && { color: colors.ink4 }
              ]}>
                {title}
              </Text>
            ) : null}
            {rightIcon && <View style={[styles.iconRight, !title && { marginLeft: 0 }]}>{rightIcon}</View>}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  buttonBase: {
    // One standard compact size for every button in the app.
    minHeight: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: 'stretch',
    paddingVertical: 0,
    paddingHorizontal: 16,
    borderWidth: 1,
  },
  // Variants — color props (bg/border/text/shadowColor) are injected from theme
  // in the component; only geometry/shadow-shape/web-transition stay here.
  primary: {
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 8,
    ...Platform.select({ web: { boxShadow: '0 10px 20px rgba(0,0,0,0.3)' } as any }),
  },
  outline: {
    borderWidth: 1.5,
  },
  danger: {
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 15,
    elevation: 6,
    ...Platform.select({ web: { boxShadow: '0 8px 15px rgba(0,0,0,0.2)' } as any }),
  },
  cancel: {
    borderWidth: 1.5,
  },
  // Premium icon-delete: calm red tint at rest, intensifies on hover. Refined
  // and unalarming vs. the solid `danger` block (kept for destructive CTAs).
  dangerSoft: {
    borderWidth: 1,
    borderRadius: 8,
    shadowOpacity: 0,
    elevation: 0,
    ...(Platform.OS === 'web' ? ({ transition: 'all 0.16s ease' } as any) : {}),
  },
  dangerSoftHovered: {
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 4px 12px rgba(239,68,68,0.18)', transform: 'translateY(-1px)' } as any)
      : {}),
  },
  ghost: {
    borderWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  google: {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 10,
    elevation: 4,
    ...Platform.select({ web: { boxShadow: '0 4px 10px rgba(0,0,0,0.3)' } as any }),
    ...(Platform.OS === 'web' ? ({ transition: 'all 0.18s ease' } as any) : {}),
  },
  googleHovered: {
    backgroundColor: "#1A73E8",
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 6px 20px rgba(26,115,232,0.5)', transform: 'translateY(-1px)' } as any)
      : {}),
  },
  // Generic premium hover lift (variant-agnostic; layers over any background).
  genericHover: {
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 12px 26px rgba(17,24,39,0.42)', transform: 'translateY(-1.5px)', filter: 'brightness(1.08)' } as any)
      : {}),
  },

  // Sizes — kept for API compatibility; matches the standard compact base so
  // every button is the same height regardless of the `size` prop.
  small: {
    minHeight: 36,
    height: 36,
    borderRadius: 10,
    paddingVertical: 0,
    paddingHorizontal: 16,
  },

  contentWrapper: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  innerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  iconLeft: {
    marginRight: 10,
  },
  iconRight: {
    marginLeft: 10,
  },
  text: {
    fontFamily: FontFamily.sans,
    fontSize: 14,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 1.5,
    textAlign: "center",
  },
  ghostText: {
    fontFamily: FontFamily.sans,
    textTransform: "none" as any,
    letterSpacing: 0,
    fontWeight: "600",
  },
  googleText: {
    fontFamily: FontFamily.sans,
    textTransform: "none" as any,
    letterSpacing: 0.3,
    fontWeight: "700",
    fontSize: 15,
  },
  smallText: {
    fontFamily: FontFamily.sans,
    fontSize: 13,
    letterSpacing: 0.2,
    fontWeight: "700",
    textTransform: "none" as any,
    textAlign: "center",
  },
});